import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBook,
  faCog,
  faCamera,
  faSave,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
  faFileUpload,
  faAward,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import mokalengProPic from '../utils/brandImages';
import StudentDocumentsPanel from '../components/profile/StudentDocumentsPanel';
import MyStatementsPanel from '../components/profile/MyStatementsPanel';

interface StudentProfile {
  userId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string | null;
  address: string;
  currentLearnership: string;
  enrollmentDate: string;
  status: number;
  profilePictureUrl: string | null;
  updatedAt: string;
}

type TabType = 'personal' | 'academic' | 'documents' | 'results' | 'account';

const studentStatusMap: { [key: number]: string } = {
  0: 'Active',
  1: 'Inactive',
  2: 'Suspended',
  3: 'Graduated',
};

export default function StudentProfilePage() {
  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  const { accessToken, updateProfilePicture } = useAuth();

  // Form state for editable fields
  const [formData, setFormData] = useState({
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/studentprofile/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data);
      setFormData({
        phoneNumber: data.phoneNumber || '',
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
        address: data.address || '',
      });
      updateProfilePicture(data.profilePictureUrl ?? null);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const response = await fetch('/api/studentprofile/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update profile');
      }

      setProfile(data.data);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingPicture(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/studentprofile/me/profile-picture', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload picture');
      }

      const data = await response.json();
      if (data.success && profile) {
        setProfile({ ...profile, profilePictureUrl: data.profilePictureUrl });
        updateProfilePicture(data.profilePictureUrl ?? null);
        setMessage({ type: 'success', text: 'Profile picture updated' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error uploading picture:', error);
      setMessage({ type: 'error', text: 'Failed to upload picture' });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <FontAwesomeIcon icon={faUser} className="h-12 w-12 text-brand-600 animate-pulse" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
          <p className="text-red-700">Failed to load profile</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Student Profile</h1>
        <p className="text-sm text-brand-900/60 mt-1">Manage your personal and academic information</p>
      </div>

      {/* Content */}
      <div className="p-8 max-w-5xl">
        {/* Alert Messages */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-2xl flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <FontAwesomeIcon
              icon={message.type === 'success' ? faCheckCircle : faExclamationCircle}
              className="h-5 w-5"
            />
            {message.text}
          </div>
        )}

          {/* Profile Header with Picture */}
          <div className="card-surface p-6 sm:p-8 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex items-center gap-6">
                {/* Profile Picture */}
                <div className="relative flex-shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brand-gradient flex items-center justify-center text-white text-xl sm:text-2xl font-bold overflow-hidden">
                    <img src={profile.profilePictureUrl || mokalengProPic} alt="Profile" className="w-full h-full object-cover" />
                  </div>
                  <label className="absolute bottom-0 right-0 bg-brand-600 hover:bg-brand-700 text-white p-2 rounded-full cursor-pointer transition-colors">
                    <FontAwesomeIcon icon={faCamera} className="h-4 w-4" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfilePictureUpload}
                      disabled={isUploadingPicture}
                    />
                  </label>
                </div>

                {/* Basic Info - shown next to picture on mobile too */}
                <div className="flex-1 min-w-0 sm:hidden">
                  <h2 className="text-lg font-bold text-brand-950 truncate">
                    {profile.firstName} {profile.lastName}
                  </h2>
                  <p className="text-sm text-brand-900/60 mt-1 truncate">{profile.studentId}</p>
                </div>
              </div>

              {/* Basic Info - desktop layout */}
              <div className="hidden sm:block flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-brand-950 truncate">
                  {profile.firstName} {profile.lastName}
                </h2>
                <p className="text-brand-900/60 mt-1">{profile.studentId}</p>
                <p className="text-sm text-brand-900/50 mt-2 truncate">{profile.email}</p>
              </div>

              {/* Email - mobile only, full width below picture row */}
              <p className="text-sm text-brand-900/50 sm:hidden truncate">{profile.email}</p>

              {/* Status Badge */}
              <div className="sm:text-right">
                <span
                  className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                    profile.status === 0
                      ? 'bg-green-100 text-green-700'
                      : profile.status === 1
                        ? 'bg-gray-100 text-gray-700'
                        : profile.status === 2
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {studentStatusMap[profile.status]}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex gap-1 border-b border-canvas-300 bg-white rounded-t-2xl overflow-x-auto">
              {[
                { id: 'personal', label: 'Personal Info', icon: faUser },
                { id: 'academic', label: 'Academic Info', icon: faBook },
                { id: 'documents', label: 'Documents', icon: faFileUpload },
                { id: 'results', label: 'Results', icon: faAward },
                { id: 'account', label: 'Account', icon: faCog },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold border-b-2 whitespace-nowrap flex-shrink-0 transition-all ${
                    activeTab === tab.id
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="card-surface p-5 sm:p-8">
            {activeTab === 'personal' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">First Name</label>
                    <input
                      type="text"
                      value={profile.firstName}
                      disabled
                      className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={profile.lastName}
                      disabled
                      className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    disabled
                    className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isEditing
                          ? 'border-canvas-300 bg-white'
                          : 'bg-canvas-100 border-canvas-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isEditing
                          ? 'border-canvas-300 bg-white'
                          : 'bg-canvas-100 border-canvas-300'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows={3}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isEditing
                        ? 'border-canvas-300 bg-white'
                        : 'bg-canvas-100 border-canvas-300'
                    }`}
                  />
                </div>
              </div>
            )}

            {activeTab === 'academic' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Current Learnership</label>
                  <input
                    type="text"
                    value={profile.currentLearnership}
                    disabled
                    className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Enrollment Date</label>
                    <input
                      type="date"
                      value={profile.enrollmentDate.split('T')[0]}
                      disabled
                      className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Status</label>
                    <input
                      type="text"
                      value={studentStatusMap[profile.status]}
                      disabled
                      className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'documents' && <StudentDocumentsPanel />}

            {activeTab === 'results' && <MyStatementsPanel />}

            {activeTab === 'account' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Student ID</label>
                  <input
                    type="text"
                    value={profile.studentId}
                    disabled
                    className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    disabled
                    className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <p className="text-sm text-blue-700">
                    To change your email or password, please contact support or use the account settings page.
                  </p>
                </div>
              </div>
            )}

            {/* Edit/Save Buttons - Only show on Personal Tab */}
            {activeTab === 'personal' && (
              <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-canvas-200">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          phoneNumber: profile.phoneNumber || '',
                          dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
                          address: profile.address || '',
                        });
                      }}
                      className="w-full sm:w-auto justify-center px-6 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="w-full sm:w-auto justify-center px-6 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="w-full sm:w-auto justify-center px-6 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                    Edit Profile
                  </button>
                )}
              </div>
            )}
          </div>
      </div>
    </>
  );
}
